import { useEffect, useState } from 'react'

import { Botao, Carregando, ErroApi, Mensagem } from '../../components/ui'
import { requisitar } from '../../lib/api'

/**
 * Console de administracao — recorte da Onda 1: leitura de usuarios, papeis e
 * trilha de auditoria, mais suspensao/reativacao. O construtor de papeis pela
 * UI e a impersonation sao da Onda 5.
 */

// ────────────────────────────────────────────────────────────── usuários

interface UsuarioLista {
  id: string
  email: string
  name: string
  status: string
  mfaEnabled: boolean
  emailVerifiedAt: string | null
  lastLoginAt: string | null
  createdAt: string
  roles: string[]
}

/**
 * Cor do status na tabela de usuarios.
 *
 * Tokens `-em-soft`, nao `--success`/`--danger` crus: o status e escrito em
 * negrito de 13px, e no tema escuro as cores cheias medem 4.03:1 e 4:1 sobre o
 * cartao — abaixo do minimo de 4.5:1 para texto pequeno. `--warning` passa
 * (5.91:1) e fica como esta.
 *
 * A cor NUNCA e a unica pista: o rotulo em `ROTULO_STATUS` diz o status por
 * escrito ao lado, que e o que serve para quem nao distingue as cores.
 */
const COR_STATUS: Record<string, string> = {
  ACTIVE: 'var(--success-em-soft)',
  PENDING_EMAIL: 'var(--warning)',
  SUSPENDED: 'var(--danger-em-soft)',
  BLOCKED: 'var(--danger-em-soft)',
}

const ROTULO_STATUS: Record<string, string> = {
  ACTIVE: 'Ativo',
  PENDING_EMAIL: 'E-mail pendente',
  SUSPENDED: 'Suspenso',
  BLOCKED: 'Bloqueado',
}

export function AdminUsuarios() {
  const [itens, setItens] = useState<UsuarioLista[]>([])
  const [busca, setBusca] = useState('')
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<unknown>(null)
  const [agindoEm, setAgindoEm] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  async function carregar(q = '') {
    setCarregando(true)
    setErro(null)
    try {
      const r = await requisitar<{ items: UsuarioLista[] }>(
        `/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      )
      setItens(r.items)
    } catch (err) {
      setErro(err)
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    void carregar()
  }, [])

  // Busca com atraso: sem isso cada tecla dispara uma consulta.
  useEffect(() => {
    const t = setTimeout(() => void carregar(busca), 350)
    return () => clearTimeout(t)
  }, [busca])

  async function alternar(u: UsuarioLista) {
    const suspender = u.status !== 'SUSPENDED'
    setAgindoEm(u.id)
    setAviso(null)
    try {
      await requisitar(`/admin/users/${u.id}/${suspender ? 'suspend' : 'reactivate'}`, {
        metodo: 'POST',
        corpo: suspender ? { reason: 'ação administrativa' } : undefined,
      })
      await carregar(busca)
    } catch (err) {
      // O 409 aqui é a invariante do último admin master (SPEC 7.2), e a
      // mensagem do servidor já explica o motivo — vale mostrá-la literalmente.
      setAviso(err instanceof Error ? err.message : 'Não foi possível concluir.')
    } finally {
      setAgindoEm(null)
    }
  }

  return (
    <div>
      <h1 className="ifr-titulo">Usuários</h1>
      <p className="ifr-apoio">Buscar, suspender e reativar contas da plataforma.</p>

      {aviso && (
        <div className="ifr-mb">
          <Mensagem tipo="warning" titulo="Ação não concluída">
            {aviso}
          </Mensagem>
        </div>
      )}
      <ErroApi erro={erro} />

      <div className="br-input ifr-largura-forma ifr-mb">
        <label htmlFor="busca">Buscar por nome ou e-mail</label>
        <input
          id="busca"
          type="search"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="ex.: maria ou maria@empresa.com"
        />
      </div>

      {carregando ? (
        <Carregando rotulo="Carregando usuários" />
      ) : itens.length === 0 ? (
        <p className="ifr-fraco">Nenhum usuário encontrado.</p>
      ) : (
        <div className="ifr-tabela-caixa">
          <table>
            <caption className="apenas-leitor">
              Usuários da plataforma, com papéis, status e ações
            </caption>
            <thead>
              <tr>
                <th scope="col">Usuário</th>
                <th scope="col">Papéis</th>
                <th scope="col">Status</th>
                <th scope="col">2FA</th>
                <th scope="col">Último acesso</th>
                <th scope="col">Ação</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="ifr-forte">{u.name}</div>
                    <div className="ifr-mini ifr-fraco">{u.email}</div>
                  </td>
                  <td>
                    <div className="ifr-tags">
                      {u.roles.map((p) => (
                        <span
                          key={p}
                          // `.ifr-tag--marca` no lugar do par inline: a classe
                          // usa `--brand-em-soft`, e `--brand` cru media 4.07:1
                          // sobre este fundo no tema escuro.
                          className="ifr-tag ifr-tag--marca"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span
                      className="ifr-forte"
                      // Cor vinda do dado (o status): estilo calculado, legitimo.
                      style={{ color: COR_STATUS[u.status] ?? 'var(--text-2)' }}
                    >
                      {/* Ponto + texto: status não pode depender só da cor. */}
                      <span aria-hidden="true">● </span>
                      {ROTULO_STATUS[u.status] ?? u.status}
                    </span>
                  </td>
                  <td>{u.mfaEnabled ? 'Sim' : '—'}</td>
                  <td>
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('pt-BR') : 'nunca'}
                  </td>
                  <td>
                    <Botao
                      variante="secondary"
                      carregando={agindoEm === u.id}
                      aoClicar={() => void alternar(u)}
                    >
                      {u.status === 'SUSPENDED' ? 'Reativar' : 'Suspender'}
                    </Botao>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────── papéis

interface PapelLista {
  id: string
  key: string
  name: string
  description: string | null
  scope: string
  isSystem: boolean
  userCount: number
  permissions: string[]
}

export function AdminPapeis() {
  const [papeis, setPapeis] = useState<PapelLista[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<unknown>(null)
  const [aberto, setAberto] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        setPapeis(await requisitar<PapelLista[]>('/admin/roles'))
      } catch (err) {
        setErro(err)
      } finally {
        setCarregando(false)
      }
    })()
  }, [])

  if (carregando) return <Carregando rotulo="Carregando papéis" />

  return (
    <div className="ifr-pagina">
      <h1 className="ifr-titulo">Papéis e permissões</h1>
      <p className="ifr-apoio">
        A matriz de acesso em vigor. Papéis de sistema não podem ser apagados; o construtor de
        papéis customizados chega na Onda 5.
      </p>

      <ErroApi erro={erro} />

      <div className="ifr-pilha">
        {papeis.map((p) => (
          <div
            key={p.id}
            className="ifr-cartao ifr-cartao--compacto"
          >
            <button
              onClick={() => setAberto(aberto === p.id ? null : p.id)}
              aria-expanded={aberto === p.id}
              className="ifr-linha-botao ifr-linha"
            >
              <div className="ifr-crescer-min">
                <div className="ifr-linha">
                  <strong className="ifr-forte-cor">{p.name}</strong>
                  <code className="ifr-mini ifr-mais-fraco">{p.key}</code>
                  {p.isSystem && (
                    <span className="ifr-tag">sistema</span>
                  )}
                </div>
                {p.description && (
                  <div className="ifr-nota ifr-fraco">
                    {p.description}
                  </div>
                )}
              </div>
              <div className="ifr-nota ifr-fraco ifr-direita">
                <div>
                  <strong className="ifr-forte-cor">{p.permissions.length}</strong> permissões
                </div>
                <div>
                  {p.userCount} {p.userCount === 1 ? 'usuário' : 'usuários'}
                </div>
              </div>
              <i
                className={`fas fa-chevron-${aberto === p.id ? 'up' : 'down'} ifr-mais-fraco`}
                aria-hidden="true"
              />
            </button>

            {aberto === p.id && (
              <div className="ifr-tags">
                {p.permissions.map((k) => (
                  <code key={k} className="ifr-etiqueta">
                    {k}
                  </code>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ───────────────────────────────────────────────────────────── auditoria

interface Log {
  id: string
  action: string
  entity: string
  entityId: string | null
  ip: string | null
  createdAt: string
  actor: { email: string; name: string } | null
}

export function AdminAuditoria() {
  const [logs, setLogs] = useState<Log[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<unknown>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await requisitar<{ items: Log[] }>('/admin/audit-logs?take=100')
        setLogs(r.items)
      } catch (err) {
        setErro(err)
      } finally {
        setCarregando(false)
      }
    })()
  }, [])

  if (carregando) return <Carregando rotulo="Carregando trilha" />

  return (
    <div>
      <h1 className="ifr-titulo">Trilha de auditoria</h1>
      <p className="ifr-apoio">
        Registro imutável — o banco recusa UPDATE e DELETE nesta tabela por gatilho.
      </p>

      <ErroApi erro={erro} />

      <div className="ifr-tabela-caixa">
        <table>
          <caption className="apenas-leitor">Ações registradas na plataforma</caption>
          <thead>
            <tr>
              <th scope="col">Quando</th>
              <th scope="col">Ação</th>
              <th scope="col">Entidade</th>
              <th scope="col">Quem</th>
              <th scope="col">IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l) => (
              <tr key={l.id}>
                <td className="ifr-nota ifr-fraco">
                  {new Date(l.createdAt).toLocaleString('pt-BR')}
                </td>
                <td>
                  <code className="ifr-nota ifr-forte-cor">{l.action}</code>
                </td>
                <td className="ifr-nota">{l.entity}</td>
                <td className="ifr-nota">{l.actor?.email ?? '(anônimo)'}</td>
                <td className="ifr-nota ifr-fraco">{l.ip ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
